import Fuse from "fuse.js";
import supabase from "./supabase";
import { extractAuthorNames } from "./scientificNameFormatter";

// 検索対象とするカラム一覧（scientific_names の全主要カラムをカバー）
export const SCIENTIFIC_NAME_SEARCH_COLUMNS = [
  // 検索用の全列（文字列化して _search_strings に含める）
  "id",
  "name_spell_valid",
  "name_spell_original",
  "authority_year",
  "authority_from_gbif",
  "authors_str",
  "current_rank",
  "current_parent",
  "current_invalid_type",
  "current_invalid_homonym_with",
  "original_rank",
  "original_parent",
  "original_invalid_type",
  "original_invalid_homonym_with",
  "valid_name_id",
  "type_taxa_id",
  "extant_fossil",
  "remark",
  "taxonomic_note",
  "type_sex",
  "type_category",
  "type_locality",
  "type_repository",
  "type_repository_id",
  "type_host",
  "source_of_original_description",
  "page",
  "gbif_id",
  "zoobank_url",
  "type_remarks",
  "last_update",
];

const TEXT_COLUMNS = [
  "id",
  "name_spell_valid",
  "name_spell_original",
  "authority_from_gbif",
  "authors_str",
  "current_rank",
  "current_parent",
  "current_invalid_type",
  "current_invalid_homonym_with",
  "original_rank",
  "original_parent",
  "original_invalid_type",
  "original_invalid_homonym_with",
  "valid_name_id",
  "type_taxa_id",
  "remark",
  "taxonomic_note",
  "type_sex",
  "type_category",
  "type_locality",
  "type_repository",
  "type_host",
  "source_of_original_description",
  "page",
  "gbif_id",
  "zoobank_url",
  "type_remarks",
];

const BOOLEAN_COLUMNS = new Set(["extant_fossil"]);
const NUMERIC_COLUMNS = new Set(["authority_year"]);
const UUID_COLUMNS = new Set(["type_repository_id"]);
const DATE_COLUMNS = new Set(["last_update"]);
const RPC_FUZZY = "search_scientific_names_fuzzy";
export const SCIENTIFIC_NAME_SEARCH_SELECT =
  "*, scientific_name_and_author!left(author_order,researcher:researcher_id(last_name,first_name))";

// 入力正規化＆トークン化（全角スペース/カンマ/コロン/セミコロン/タブを統一）
export const normalizeSearchQuery = (raw = "") =>
  (raw ?? "")
    .toString()
    .normalize("NFKC")
    .replace(/[\u3000]/g, " ")
    .replace(/[,\uFF0C、:：;；\t]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const tokenizeSearchQuery = (raw = "") => {
  const normalized = normalizeSearchQuery(raw);
  if (!normalized) return [];
  return normalized.split(" ").filter(Boolean);
};

const escapeCommas = (value = "") => value.replace(/,/g, "\\,");

const makeOrClause = (token, matchMode) => {
  const escaped = escapeCommas(token);
  const likeValue = matchMode === "partial" ? `%${escaped}%` : escaped;
  const clauses = [];

  TEXT_COLUMNS.forEach((col) => {
    clauses.push(`${col}.ilike.${likeValue}`);
  });

  if (/^[0-9a-fA-F-]{8,}$/.test(token)) {
    UUID_COLUMNS.forEach((col) => clauses.push(`${col}.eq.${escaped}`));
  }

  if (!Number.isNaN(Number(token))) {
    NUMERIC_COLUMNS.forEach((col) => clauses.push(`${col}.eq.${escaped}`));
  }

  if (!Number.isNaN(Date.parse(token))) {
    DATE_COLUMNS.forEach((col) => clauses.push(`${col}.eq.${escaped}`));
  }

  const boolStr = token.toLowerCase();
  if (["true", "false", "extant", "fossil"].includes(boolStr)) {
    const val = boolStr === "true" || boolStr === "extant";
    BOOLEAN_COLUMNS.forEach((col) => clauses.push(`${col}.eq.${val}`));
  }

  return clauses.join(",");
};

const dedupeRows = (rows = []) => {
  const seen = new Set();
  const result = [];
  rows.forEach((row) => {
    const key = row?.id || JSON.stringify(row);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(row);
  });
  return result;
};

const flattenToStrings = (value, collector) => {
  if (value === null || value === undefined) return;
  if (Array.isArray(value)) {
    value.forEach((v) => flattenToStrings(v, collector));
    return;
  }
  if (typeof value === "object") {
    Object.values(value).forEach((v) => flattenToStrings(v, collector));
    return;
  }
  const str = String(value).normalize("NFKC").toLowerCase().trim();
  if (str) collector.push(str);
};

const buildSearchStrings = (row = {}) => {
  const values = [];
  SCIENTIFIC_NAME_SEARCH_COLUMNS.forEach((col) => {
    if (row[col] !== undefined && row[col] !== null) values.push(row[col]);
  });
  // RPC から返る search_text / authors_arr なども検索対象に含める
  if (row.search_text) values.push(row.search_text);
  if (Array.isArray(row.authors_arr) && row.authors_arr.length) {
    values.push(row.authors_arr.join(" "));
  }
  if (row.authors_text) values.push(row.authors_text);
  // 親のラベルや著者名も検索対象に含める
  if (row._parent_label) values.push(row._parent_label);
  const authors = extractAuthorNames(row);
  if (authors.length) values.push(authors.join(" "));
  const strings = [];
  values.forEach((v) => flattenToStrings(v, strings));
  return strings;
};

const attachSearchMeta = (rows = []) =>
  rows.map((row) => {
    const strings = buildSearchStrings(row);
    return {
      ...row,
      _authority_authors: extractAuthorNames(row),
      _search_strings: strings,
      _search_blob: strings.join(" "),
    };
  });

const applyClientFilters = (rows, { tokens, combineMode, matchMode, allowFuzzy, query, limit }) => {
  const normalizedTokens = (tokens || []).map((t) => t.toLowerCase());
  const matchesToken = (row, token) => {
    if (!token) return true;
    const strings = row._search_strings || [];
    if (matchMode === "exact") return strings.some((s) => s === token);
    return strings.some((s) => s.includes(token));
  };

  const baseMatched =
    normalizedTokens.length === 0
      ? rows
      : rows.filter((row) =>
          combineMode === "OR"
            ? normalizedTokens.some((t) => matchesToken(row, t))
            : normalizedTokens.every((t) => matchesToken(row, t))
        );

  const fuseResults =
    allowFuzzy && query
      ? new Fuse(rows, {
          keys: ["_search_blob"],
          threshold: matchMode === "exact" ? 0.22 : 0.35,
          ignoreLocation: true,
          minMatchCharLength: query.length > 3 ? 2 : 1,
        }).search(query)
      : [];

  const merged = dedupeRows([
    ...baseMatched,
    ...fuseResults.map((r) => r.item),
  ]);

  return typeof limit === "number" ? merged.slice(0, limit) : merged;
};

const fetchParents = async (rows = []) => {
  const parentIds = Array.from(
    new Set(rows.map((r) => r?.current_parent).filter(Boolean))
  );
  if (!parentIds.length) return rows;
  try {
    const { data, error } = await supabase
      .from("scientific_names")
      .select("id, name_spell_valid, name_spell_original")
      .in("id", parentIds);
    if (error) throw error;
    const map = new Map(
      (data || []).map((p) => [p.id, p.name_spell_valid || p.name_spell_original || p.id])
    );
    return rows.map((r) => ({
      ...r,
      _parent_label: r._parent_label || map.get(r.current_parent) || "",
    }));
  } catch (e) {
    console.warn("[scientificNameSearch] parent fetch failed", e.message || e);
    return rows;
  }
};

const fetchByIds = async (ids = []) => {
  const uniq = Array.from(new Set(ids.filter(Boolean)));
  if (!uniq.length) return [];
  const { data, error } = await supabase
    .from("scientific_names")
    .select(SCIENTIFIC_NAME_SEARCH_SELECT)
    .in("id", uniq);
  if (error) throw error;
  return data || [];
};

const fetchByTokens = async ({ tokens, combineMode, matchMode, limit }) => {
  if (!tokens.length) return [];

  // OR は一括、AND はトークンごとに拾い上げてクライアントで AND フィルタ
  if (combineMode === "OR") {
    const orClause = tokens.map((t) => makeOrClause(t, matchMode)).filter(Boolean).join(",");
    if (!orClause) return [];
    const { data, error } = await supabase
      .from("scientific_names")
      .select(SCIENTIFIC_NAME_SEARCH_SELECT)
      .order("name_spell_valid", { ascending: true })
      .or(orClause)
      .limit(limit);
    if (error) throw error;
    return data || [];
  }

  // AND: 各トークンで個別クエリを実行し、統合する
  const buckets = [];
  for (const t of tokens) {
    const clause = makeOrClause(t, matchMode);
    if (!clause) continue;
    const { data, error } = await supabase
      .from("scientific_names")
      .select(SCIENTIFIC_NAME_SEARCH_SELECT)
      .order("name_spell_valid", { ascending: true })
      .or(clause)
      .limit(limit);
    if (error) throw error;
    buckets.push(...(data || []));
  }
  return dedupeRows(buckets);
};

const fetchFuzzy = async (query) => {
  try {
    // v2 RPC を優先的に使う
    const { data, error } = await supabase.rpc("search_scientific_names_v2", {
      p_query: query,
      p_combine: "OR",
      p_match_mode: "partial",
      p_allow_fuzzy: true,
      p_limit: 200,
      p_offset: 0,
    });
    if (error) throw error;
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.warn("[scientificNameSearch] fuzzy RPC failed (v2 fallback to old)", e.message || e);
    try {
      const { data, error } = await supabase.rpc(RPC_FUZZY, { q: query });
      if (error) throw error;
      return Array.isArray(data) ? data : [];
    } catch (ee) {
      console.warn("[scientificNameSearch] old fuzzy RPC failed", ee.message || ee);
      return [];
    }
  }
};

export async function searchScientificNames({
  query,
  combineMode = "AND",
  matchMode = "partial",
  allowFuzzy = false,
  limit = 50,
  offset = 0,
} = {}) {
  const normalized = normalizeSearchQuery(query);
  const tokens = tokenizeSearchQuery(normalized);
  if (!normalized) return { results: [], tokens, normalized, hasMore: false };

  let candidates = [];
  let hasMore = false;

  // まず v2 RPC で完結させる（fuzzy/AND/OR/部分/完全）
  try {
    const { data, error } = await supabase.rpc("search_scientific_names_v2", {
      p_query: normalized,
      p_combine: combineMode,
      p_match_mode: matchMode,
      p_allow_fuzzy: allowFuzzy,
      p_limit: limit,
      p_offset: offset,
    });
    if (error) throw error;
    const rows = Array.isArray(data) ? data : [];
    candidates = rows;
    hasMore = rows.length >= limit;
  } catch (e) {
    const msg = e?.message || String(e);
    console.warn("[scientificNameSearch] v2 RPC failed", msg);
    // statement timeout 対策: fuzzy を切って limit を絞りリトライ
    if (msg.toLowerCase().includes("timeout")) {
      try {
        const { data } = await supabase.rpc("search_scientific_names_v2", {
          p_query: normalized,
          p_combine: combineMode,
          p_match_mode: matchMode,
          p_allow_fuzzy: false,
          p_limit: Math.min(limit, 30),
          p_offset: offset,
        });
        candidates = Array.isArray(data) ? data : [];
        hasMore = candidates.length >= Math.min(limit, 30);
      } catch (ee) {
        console.warn("[scientificNameSearch] retry without fuzzy failed", ee?.message || ee);
      }
    }

    // v2 が取得できなかった場合のみ旧ロジックにフォールバック
    if (!candidates.length) {
      if (allowFuzzy) {
        const fuzzyRows = await fetchFuzzy(normalized);
        if (fuzzyRows.length) {
          const ids = fuzzyRows.map((r) => r?.id).filter(Boolean);
          if (ids.length) {
            try {
              const hydrated = await fetchByIds(ids);
              const map = new Map((hydrated || []).map((r) => [r.id, r]));
              const merged = fuzzyRows.map((r) => ({ ...map.get(r.id), ...r }));
              candidates = dedupeRows([...merged, ...hydrated]);
            } catch (ee) {
              console.warn("[scientificNameSearch] hydrate fuzzy rows failed", ee.message || ee);
              candidates = fuzzyRows;
            }
          } else {
            candidates = fuzzyRows;
          }
          hasMore = candidates.length >= limit;
        }
      }

      if (!candidates.length) {
        const tokensForFetch = tokens.length ? tokens : [normalized];
        try {
          candidates = await fetchByTokens({
            tokens: tokensForFetch,
            combineMode,
            matchMode,
            limit: Math.min(limit, 30),
          });
          hasMore = candidates.length >= Math.min(limit, 30);
        } catch (ee) {
          console.warn("[scientificNameSearch] token fetch failed", ee.message || ee);
          candidates = [];
          hasMore = false;
        }
      } else if (tokens.length) {
        try {
          const strict = await fetchByTokens({ tokens, combineMode, matchMode, limit: Math.min(limit, 30) });
          candidates = dedupeRows([...candidates, ...strict]);
        } catch {
          // keep existing
        }
      }
    }
  }

  if (!candidates.length) return { results: [], tokens, normalized, hasMore: false };

  const withParents = await fetchParents(candidates);
  const enriched = attachSearchMeta(withParents);
  const results = applyClientFilters(enriched, {
    tokens,
    combineMode,
    matchMode,
    allowFuzzy,
    query: normalized,
    limit,
  });

  return { results, tokens, normalized, hasMore };
}
